/**
 * CNC export — machine profiles on the host side: checking one before it is
 * sent, and keeping the user's profiles and last-used header in localStorage.
 *
 * Pure apart from the storage helpers, which take a `Storage`-like object (the
 * check script passes an in-memory one) and never throw: a private window or
 * blocked storage just means nothing is remembered.
 *
 * WHY THE HOST CHECKS A PROFILE AT ALL. The viewer does not validate the
 * profile it is handed; a malformed one either throws deep inside the post
 * (a rejected RPC with a stack-trace message) or, worse, emits a program from
 * nonsense numbers that the gate then has to catch. The gate re-parses the
 * TEXT, so it catches what reaches the file — but a sign of 0, a 400° C sweep
 * or one decimal place are not things a user should be able to send in the
 * first place. Everything here is a REFUSAL with a plain-words reason; the
 * profile is never "fixed up" silently.
 *
 * Since v66's review the viewer ALSO refuses an unsafe profile itself
 * (`cncProfileProblems` in `band/cnc/profile.ts`), because any RPC caller can
 * send one; the safety floors below are the same numbers, kept in step:
 * approach ≥ 0.1 mm, lift ≥ 0.05 mm, a pass ≤ half its cutter's diameter,
 * min wall ≥ 0.1 mm, no two cutters on one (H, work offset) pair, and a
 * shaped seat row (a `cut` other than round) pocketed by a flat end mill at a
 * 0–1 mm clearance, which no round row carries.
 *
 * The Vault API server checks a workspace's saved profiles with this SAME
 * `validateCncProfile` (`server/modules/integrations/cnc/`), so this file is
 * compiled by `tsconfig.server.json` as well: relative imports end in `.js`
 * (Node module resolution) and nothing here may use the DOM's globals.
 * `npm run lint:server` fails if either slips.
 */
import type { CncMachineProfile, CncSizeSystem, CncToolKind } from "../../../shared/api-contract.js";

/** The gate's own floor (`verify/gate.ts` `AIR_CUT_MIN_GAP_MM`): an air cut closer than this is not an air cut. */
export const CNC_AIR_CUT_MIN_MM = 0.5;
/** Fanuc extended work offsets `G54.1P1` … `G54.1P48` (`post/format.ts` `CNC_MAX_EXTENDED_WCS`). */
const MAX_EXTENDED_WCS = 48;
/** The viewer's own floors (`band/cnc/profile.ts`): below these it refuses the profile anyway. */
export const CNC_MIN_APPROACH_MM = 0.1;
export const CNC_MIN_LIFT_MM = 0.05;
export const CNC_MIN_WALL_FLOOR_MM = 0.1;
export const CNC_MAX_STEP_FRACTION = 0.5;

const TOOL_KINDS: readonly CncToolKind[] = ["conical", "flat", "ball", "vbit", "drill"];
/** A seat row's `cut`: the band builder's cut id (`band/cnc/profile.ts` `cncProfileProblems` reads the same). */
export const CNC_CUT_ID = /^[a-z][a-z0-9_-]*$/;
const SIZE_SYSTEMS: readonly CncSizeSystem[] = ["US", "EU", "UK", "IN"];

export type CncProfileCheck =
  | { ok: true; profile: CncMachineProfile }
  | { ok: false; errors: string[] };

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * Check a value (usually `JSON.parse` of the Advanced editor) is a usable
 * `CncMachineProfile`. Every problem is listed, each naming the field.
 */
export function validateCncProfile(value: unknown): CncProfileCheck {
  const errors: string[] = [];
  const err = (path: string, msg: string) => errors.push(`${path}: ${msg}`);

  if (!isObj(value)) return { ok: false, errors: ["The profile must be a JSON object."] };
  const p = value;

  const str = (o: Obj, k: string, path: string, opts: { empty?: boolean } = {}) => {
    const v = o[k];
    if (typeof v !== "string") err(path, "must be text");
    else if (!opts.empty && v.length === 0) err(path, "cannot be empty");
  };
  const num = (o: Obj, k: string, path: string, lo: number, hi: number, opts: { int?: boolean; loOpen?: boolean } = {}) => {
    const v = o[k];
    if (!isNum(v)) return err(path, "must be a number");
    if (opts.int && !Number.isInteger(v)) return err(path, "must be a whole number");
    if (opts.loOpen ? v <= lo : v < lo) return err(path, `must be ${opts.loOpen ? "more than" : "at least"} ${lo}`);
    if (v > hi) err(path, `must be at most ${hi}`);
  };
  const bool = (o: Obj, k: string, path: string) => {
    if (typeof o[k] !== "boolean") err(path, "must be true or false");
  };
  const section = (k: string): Obj | null => {
    const v = p[k];
    if (!isObj(v)) {
      err(k, "is missing or not an object");
      return null;
    }
    return v;
  };

  // ── Identity and calibration ──
  str(p, "name", "name");
  bool(p, "calibrated", "calibrated");
  if (p.calibrated === true) {
    // Marking a profile proven is a claim about a real machine; it has to say who and when.
    if (typeof p.calibratedBy !== "string" || p.calibratedBy.trim() === "") {
      err("calibratedBy", "say who proved this profile at the shop before marking it calibrated");
    }
    if (typeof p.calibratedAt !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(p.calibratedAt)) {
      err("calibratedAt", "give the prove-out date (YYYY-MM-DD) before marking it calibrated");
    }
  }

  // ── Framing: the dialect's fixed lines ──
  const f = section("framing");
  if (f) {
    if (f.eol !== "\r\n" && f.eol !== "\n") err("framing.eol", 'must be "\\r\\n" or "\\n"');
    for (const k of ["header", "toolChange", "firstOpStart", "opStart", "lengthComp", "comment", "drillCycle", "drillCancel"]) {
      str(f, k, `framing.${k}`);
    }
    if (typeof f.header === "string") {
      for (const key of ["{LABEL}", "{SIZE}", "{MODEL}"]) {
        if (!f.header.includes(key)) err("framing.header", `must contain ${key}`);
      }
    }
    // The op-start lines carry each tool's work offset and the start position.
    for (const k of ["firstOpStart", "opStart"]) {
      const v = f[k];
      if (typeof v === "string") for (const key of ["{WCS}", "{Y}", "{C}"]) if (!v.includes(key)) err(`framing.${k}`, `must contain ${key}`);
    }
    if (typeof f.toolChange === "string" && !f.toolChange.includes("{T}")) err("framing.toolChange", "must contain {T}");
    if (typeof f.lengthComp === "string") {
      for (const key of ["{H}", "{Z}"]) if (!f.lengthComp.includes(key)) err("framing.lengthComp", `must contain ${key}`);
    }
    if (typeof f.comment === "string" && !f.comment.includes("{TEXT}")) err("framing.comment", "must contain {TEXT}");
    if (f.firstToolComment !== null && typeof f.firstToolComment !== "string") err("framing.firstToolComment", "must be text or null");
    for (const k of ["safeStart", "initialHome", "betweenTools", "footer"]) {
      const v = f[k];
      if (!Array.isArray(v) || !v.every((l) => typeof l === "string")) err(`framing.${k}`, "must be a list of lines");
    }
  }

  // ── Numbers ──
  const n = section("numbers");
  if (n) {
    // Fewer than 3 decimals rounds every coordinate to 0.01 mm or coarser — not a ring.
    num(n, "decimals", "numbers.decimals", 3, 6, { int: true });
    bool(n, "leadingZero", "numbers.leadingZero");
    bool(n, "trailingDot", "numbers.trailingDot");
  }

  // ── Kinematics ──
  const k = section("kinematics");
  if (k) {
    if (k.signC !== 1 && k.signC !== -1) err("kinematics.signC", "must be 1 or -1");
    if (k.signY !== 1 && k.signY !== -1) err("kinematics.signY", "must be 1 or -1");
    // At 180° or more a controller that rolls C over may take the short way round.
    if (!isNum(k.maxCPerBlockDeg) || k.maxCPerBlockDeg <= 0 || k.maxCPerBlockDeg >= 180) {
      err("kinematics.maxCPerBlockDeg", "must be more than 0 and less than 180 degrees");
    }
    bool(k, "allowSimultaneousZ", "kinematics.allowSimultaneousZ");
    if (k.feedMode !== "G94-raw") err("kinematics.feedMode", 'must be "G94-raw" (the only feed rule the generator knows)');
  }

  // ── Motion law ──
  const m = section("motion");
  if (m) {
    // A zero approach rapids straight onto uncut metal; a zero lift rapids
    // off a floor still touching it. The viewer refuses both as well.
    num(m, "approachMM", "motion.approachMM", CNC_MIN_APPROACH_MM, 5);
    num(m, "liftMM", "motion.liftMM", CNC_MIN_LIFT_MM, 5);
  }

  // ── Tools ──
  const toolKinds = new Map<string, CncToolKind>();
  /** (H, work offset) → the tool on it: every tool runs on H1, so that pair IS its length. */
  const zeroes = new Map<string, string>();
  if (!Array.isArray(p.tools) || p.tools.length === 0) {
    err("tools", "must list at least one cutter");
  } else {
    p.tools.forEach((t, i) => {
      const path = `tools[${i}]`;
      if (!isObj(t)) return err(path, "must be an object");
      const id = typeof t.id === "string" ? t.id : "";
      const at = id ? `${path} (${id})` : path;
      if (!id) err(path, "needs an id");
      else if (toolKinds.has(id)) err(at, "has the same id as another tool");
      if (t.source !== "shop" && t.source !== "assumed") err(`${at}.source`, 'must be "shop" or "assumed"');
      num(t, "t", `${at}.t`, 0, 999, { int: true });
      num(t, "h", `${at}.h`, 0, 999, { int: true });
      if (typeof t.wcs !== "string" || !isWcs(t.wcs)) err(`${at}.wcs`, `must be G54…G59 or G54.1P1…G54.1P${MAX_EXTENDED_WCS}`);
      else if (isNum(t.h)) {
        const pair = `H${t.h} ${t.wcs}`;
        const other = zeroes.get(pair);
        if (other !== undefined) err(`${at}.wcs`, `shares ${pair} with "${other}" — two cutters on one length offset run on each other's length`);
        else zeroes.set(pair, id || path);
      }
      if (!TOOL_KINDS.includes(t.kind as CncToolKind)) err(`${at}.kind`, `must be one of ${TOOL_KINDS.join(", ")}`);
      else if (id) toolKinds.set(id, t.kind as CncToolKind);
      num(t, "diameterMM", `${at}.diameterMM`, 0, 20, { loOpen: true });
      if (typeof t.label !== "string" || t.label.length === 0) err(`${at}.label`, "cannot be empty");
      // The label is printed inside a ( … ) comment: a parenthesis would end it early.
      else if (/[()\r\n]/.test(t.label)) err(`${at}.label`, "cannot contain parentheses or line breaks");
      num(t, "bDeg", `${at}.bDeg`, -360, 360);
      num(t, "spindleRpm", `${at}.spindleRpm`, 0, 100000, { int: true, loOpen: true });
      str(t, "spindleWords", `${at}.spindleWords`);
      num(t, "plungeFeed", `${at}.plungeFeed`, 0, 20000, { loOpen: true });
      num(t, "cutFeed", `${at}.cutFeed`, 0, 20000, { loOpen: true });
      if (t.liftFeed !== undefined) num(t, "liftFeed", `${at}.liftFeed`, 0, 20000, { loOpen: true });
      num(t, "clearanceAboveMM", `${at}.clearanceAboveMM`, 0, 50, { loOpen: true });
      num(t, "maxStepDownMM", `${at}.maxStepDownMM`, 0, 2, { loOpen: true });
      if (isNum(t.maxStepDownMM) && isNum(t.diameterMM) && t.diameterMM > 0 && t.maxStepDownMM > CNC_MAX_STEP_FRACTION * t.diameterMM + 1e-12) {
        err(`${at}.maxStepDownMM`, `${t.maxStepDownMM} bites more than ${CNC_MAX_STEP_FRACTION}× its Ø${t.diameterMM} per pass`);
      }
      for (const opt of ["includedAngleDeg", "tipDiameterMM", "fluteLengthMM"]) {
        if (t[opt] !== undefined) num(t, opt, `${at}.${opt}`, 0, 180, { loOpen: opt !== "tipDiameterMM" });
      }
      if (t.kind === "drill") {
        if (!isObj(t.drill)) err(`${at}.drill`, "a drill needs its cycle ({ feed, cycle })");
        else {
          num(t.drill, "feed", `${at}.drill.feed`, 0, 20000, { loOpen: true });
          str(t.drill, "cycle", `${at}.drill.cycle`);
        }
      }
    });
  }

  // ── Seat standard ──
  if (!Array.isArray(p.seatStandard)) {
    err("seatStandard", "must be a list (it may be empty)");
  } else {
    p.seatStandard.forEach((s, i) => {
      const path = `seatStandard[${i}]`;
      if (!isObj(s)) return err(path, "must be an object");
      num(s, "stoneMM", `${path}.stoneMM`, 0, 20, { loOpen: true });
      num(s, "seatDepthMM", `${path}.seatDepthMM`, 0, 10, { loOpen: true });
      num(s, "drillDepthMM", `${path}.drillDepthMM`, 0, 10);
      if (s.source !== "shop" && s.source !== "derived") err(`${path}.source`, 'must be "shop" or "derived"');
      const seatKind = typeof s.seatToolId === "string" ? toolKinds.get(s.seatToolId) : undefined;
      if (!seatKind) err(`${path}.seatToolId`, "must name a tool in this profile");
      else if (seatKind === "drill") err(`${path}.seatToolId`, "must be a cutter, not a drill");
      // A shaped stone (v66 `cut`) is seated in a POCKET round its girdle: a
      // flat end mill at a clearance. The viewer's `cncProfileProblems` refuses
      // the same rows, so the host never sends one it would throw on.
      if (s.cut !== undefined && (typeof s.cut !== "string" || !CNC_CUT_ID.test(s.cut))) {
        err(`${path}.cut`, "must be a cut id (lower-case letters, digits, - or _)");
      } else if (s.cut !== undefined && s.cut !== "round") {
        if (!isNum(s.clearanceMM) || s.clearanceMM < 0 || s.clearanceMM > 1) err(`${path}.clearanceMM`, "a shaped stone's pocket needs its clearance, 0 to 1 mm");
        if (seatKind && seatKind !== "drill" && seatKind !== "flat") err(`${path}.seatToolId`, "a shaped stone's pocket is cut with a flat end mill");
      } else if (s.clearanceMM !== undefined) {
        err(`${path}.clearanceMM`, "only a shaped stone's row (a cut other than round) has a pocket clearance");
      }
      if (s.drillToolId !== null) {
        const drillKind = typeof s.drillToolId === "string" ? toolKinds.get(s.drillToolId) : undefined;
        if (drillKind !== "drill") err(`${path}.drillToolId`, "must name a drill in this profile, or be null");
      } else if (s.drillDepthMM !== 0) {
        err(`${path}.drillDepthMM`, "must be 0 when there is no drill");
      }
    });
  }

  // ── Limits, size system, air cut ──
  const l = section("limits");
  if (l) {
    num(l, "minWallMM", "limits.minWallMM", CNC_MIN_WALL_FLOOR_MM, 10);
    num(l, "minWebMM", "limits.minWebMM", 0, 10);
  }
  if (!SIZE_SYSTEMS.includes(p.sizeSystem as CncSizeSystem)) err("sizeSystem", "must be US, EU, UK or IN");
  num(p, "airCutOffsetMM", "airCutOffsetMM", CNC_AIR_CUT_MIN_MM, 50);

  return errors.length ? { ok: false, errors } : { ok: true, profile: value as unknown as CncMachineProfile };
}

function isWcs(w: string): boolean {
  if (/^G5[4-9]$/.test(w)) return true;
  const m = /^G54\.1P([1-9]\d*)$/.exec(w);
  return !!m && Number(m[1]) <= MAX_EXTENDED_WCS;
}

// ── localStorage ────────────────────────────────────────────────────────────

/** The part of `Storage` the helpers use, so the check script can pass a Map-backed one. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const CNC_PROFILES_KEY = "ringBuilder.cnc.profiles.v1";
export const CNC_PREFS_KEY = "ringBuilder.cnc.prefs.v1";

/** The user's saved profiles and which one is in use. */
export interface CncProfileShelf {
  selected: string;
  profiles: CncMachineProfile[];
}

/** The header and options the dialog remembers between exports. */
export interface CncPrefs {
  label: string;
  model: string;
  sizeSystem: CncSizeSystem;
  /** Draw rapids in the preview as well as cutting moves. */
  rapids: boolean;
  /** Ask for the `.cam.3dm` with each check (the viewer loads rhino3dm from a CDN for it). */
  cam3dm: boolean;
}

export const DEFAULT_CNC_PREFS: CncPrefs = { label: "", model: "", sizeSystem: "US", rapids: true, cam3dm: true };

function browserStore(): KeyValueStore | null {
  try {
    // Read through globalThis: the server's program has no DOM `window` type.
    const win = (globalThis as { window?: { localStorage: KeyValueStore } }).window;
    return win ? win.localStorage : null;
  } catch {
    return null;
  }
}

function readJson(store: KeyValueStore | null, key: string): unknown {
  try {
    const raw = store?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(store: KeyValueStore | null, key: string, value: unknown): void {
  try {
    store?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked — the dialog still works, it just forgets */
  }
}

/**
 * The saved profiles. A stored profile that no longer passes
 * `validateCncProfile` (hand-edited storage, an older shape) is DROPPED, never
 * sent — the dialog then falls back to the shop's sample profile.
 */
export function loadCncShelf(store: KeyValueStore | null = browserStore()): CncProfileShelf | null {
  const raw = readJson(store, CNC_PROFILES_KEY);
  if (!isObj(raw) || !Array.isArray(raw.profiles)) return null;
  const profiles: CncMachineProfile[] = [];
  for (const p of raw.profiles) {
    const check = validateCncProfile(p);
    if (check.ok && !profiles.some((q) => q.name === check.profile.name)) profiles.push(check.profile);
  }
  if (profiles.length === 0) return null;
  const selected = typeof raw.selected === "string" && profiles.some((p) => p.name === raw.selected) ? raw.selected : profiles[0].name;
  return { selected, profiles };
}

export function saveCncShelf(shelf: CncProfileShelf, store: KeyValueStore | null = browserStore()): void {
  writeJson(store, CNC_PROFILES_KEY, shelf);
}

export function loadCncPrefs(store: KeyValueStore | null = browserStore()): CncPrefs {
  const raw = readJson(store, CNC_PREFS_KEY);
  if (!isObj(raw)) return { ...DEFAULT_CNC_PREFS };
  return {
    label: typeof raw.label === "string" ? raw.label : DEFAULT_CNC_PREFS.label,
    model: typeof raw.model === "string" ? raw.model : DEFAULT_CNC_PREFS.model,
    sizeSystem: SIZE_SYSTEMS.includes(raw.sizeSystem as CncSizeSystem) ? (raw.sizeSystem as CncSizeSystem) : DEFAULT_CNC_PREFS.sizeSystem,
    rapids: typeof raw.rapids === "boolean" ? raw.rapids : DEFAULT_CNC_PREFS.rapids,
    cam3dm: typeof raw.cam3dm === "boolean" ? raw.cam3dm : DEFAULT_CNC_PREFS.cam3dm,
  };
}

export function saveCncPrefs(prefs: CncPrefs, store: KeyValueStore | null = browserStore()): void {
  writeJson(store, CNC_PREFS_KEY, prefs);
}

/** A name not yet on the shelf: `base`, else `base 2`, `base 3` … */
export function uniqueProfileName(base: string, shelf: CncProfileShelf): string {
  const taken = new Set(shelf.profiles.map((p) => p.name));
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) if (!taken.has(`${base} ${i}`)) return `${base} ${i}`;
}
